// Pure helpers for the Experts directory and the community Wishlist. No
// React/store imports.
import type { ExpertCard, ExpertService, ListingKind, WishView } from '@dunea/shared';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

// ─── experts ───

/** Markets the directory filters by (ISO 3166-1 alpha-2). */
export const EXPERT_COUNTRIES = [
  ['US', 'United States'],
  ['GB', 'United Kingdom'],
  ['IN', 'India'],
  ['AE', 'United Arab Emirates'],
  ['SA', 'Saudi Arabia'],
  ['CA', 'Canada'],
  ['AU', 'Australia'],
  ['DE', 'Germany'],
  ['FR', 'France'],
  ['NL', 'Netherlands'],
  ['SG', 'Singapore'],
  ['PK', 'Pakistan'],
  ['NG', 'Nigeria'],
  ['BR', 'Brazil'],
] as const;

const COUNTRY_NAMES = new Map<string, string>(EXPERT_COUNTRIES);

export function countryName(code: string): string {
  return COUNTRY_NAMES.get(code.toUpperCase()) ?? code.toUpperCase();
}

/** Minor units → "$450", "₹12,000", "$9.50". Unknown currency falls back to USD. */
export function moneyLabel(cents: number, currency: string | null | undefined): string {
  const major = (Number.isFinite(cents) ? cents : 0) / 100;
  const digits = Number.isInteger(major) ? 0 : 2;
  const format = (code: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(major);

  try {
    return format(currency || 'USD');
  } catch {
    return format('USD');
  }
}

/** "From $450" for a card, or null when the expert lists no prices. */
export function expertFromLabel(card: Pick<ExpertCard, 'fromCents' | 'currency'>): string | null {
  return typeof card.fromCents === 'number' && card.fromCents > 0
    ? `From ${moneyLabel(card.fromCents, card.currency)}`
    : null;
}

export function serviceLine(service: Pick<ExpertService, 'priceFromCents' | 'currency' | 'deliveryDays'>): string {
  const days = service.deliveryDays === 1 ? '1 day' : `${service.deliveryDays} days`;
  return `From ${moneyLabel(service.priceFromCents, service.currency)} · ${days}`;
}

export function completedLabel(n: number): string {
  if (!n) {
    return 'No completed projects yet';
  }

  return n === 1 ? '1 project completed' : `${n} projects completed`;
}

/** Portfolio links: https only, at most 12. */
export function portfolioLinks(urls: readonly string[] | null | undefined): { href: string; label: string }[] {
  const out: { href: string; label: string }[] = [];

  for (const raw of urls ?? []) {
    try {
      const u = new URL(raw);

      if (u.protocol === 'https:') {
        out.push({
          href: u.toString(),
          label: u.hostname.replace(/^www\./, '') + (u.pathname === '/' ? '' : u.pathname),
        });
      }
    } catch {
      // drop it
    }

    if (out.length >= 12) {
      break;
    }
  }

  return out;
}

export interface ExpertRequestDraft {
  message: string;
  service: string;
  /** Major units as typed ("500"). */
  budget: string;
  attachApp: boolean;
}

export const REQUEST_MESSAGE_MIN = 20;
export const REQUEST_MESSAGE_MAX = 2000;

/** Validate the request form. Budget is optional, whole currency units. */
export function expertRequestErrors(draft: ExpertRequestDraft): Partial<Record<'message' | 'budget', string>> {
  const errors: Partial<Record<'message' | 'budget', string>> = {};
  const message = draft.message.trim();

  if (message.length < REQUEST_MESSAGE_MIN) {
    errors.message = `Tell them what you need (at least ${REQUEST_MESSAGE_MIN} characters).`;
  } else if (message.length > REQUEST_MESSAGE_MAX) {
    errors.message = `Keep it under ${REQUEST_MESSAGE_MAX} characters.`;
  }

  const budget = draft.budget.trim().replace(/[,$\s]/g, '');

  if (budget && !(/^\d+(\.\d{1,2})?$/.test(budget) && Number(budget) > 0)) {
    errors.budget = 'Enter an amount, like 500.';
  }

  return errors;
}

/** The POST body for a request (budget in cents; the app only when asked). */
export function expertRequestBody(
  draft: ExpertRequestDraft,
  appId: string | null,
): { message: string; service?: string; budgetCents?: number; appId?: string } {
  const budget = draft.budget.trim().replace(/[,$\s]/g, '');

  return {
    message: draft.message.trim(),
    ...(draft.service ? { service: draft.service } : {}),
    ...(budget ? { budgetCents: Math.round(Number(budget) * 100) } : {}),
    ...(draft.attachApp && appId ? { appId } : {}),
  };
}

// ─── wishlist ───

export const WISH_SORTS = [
  ['top', 'Top'],
  ['new', 'New'],
] as const;

export const WISH_KINDS = [
  ['plugin', 'Plugin'],
  ['theme', 'Theme'],
  ['template', 'Template'],
  ['skill', 'Skill'],
] as const satisfies readonly (readonly [ListingKind, string])[];

const WISH_STATUS: Record<WishView['status'], { label: string; tone: Tone }> = {
  open: { label: 'Open', tone: 'muted' },
  building: { label: 'Being built', tone: 'info' },
  fulfilled: { label: 'Available', tone: 'success' },
  closed: { label: 'Closed', tone: 'muted' },
};

export function wishStatusMeta(status: string): { label: string; tone: Tone } {
  return WISH_STATUS[status as WishView['status']] ?? { label: status, tone: 'muted' };
}

export const WISH_TITLE_MIN = 8;
export const WISH_TITLE_MAX = 120;
export const WISH_BODY_MAX = 1000;

export function wishDraftError(draft: { title: string; body: string }): string | null {
  const title = draft.title.trim();

  if (title.length < WISH_TITLE_MIN) {
    return `Describe it in at least ${WISH_TITLE_MIN} characters.`;
  }

  if (title.length > WISH_TITLE_MAX) {
    return `Keep the title under ${WISH_TITLE_MAX} characters.`;
  }

  if (draft.body.length > WISH_BODY_MAX) {
    return `Keep the details under ${WISH_BODY_MAX} characters.`;
  }

  return null;
}

/** Optimistic vote toggle (the server's answer replaces it). */
export function toggleVote(wish: WishView): WishView {
  return wish.votedByMe
    ? { ...wish, votedByMe: false, voteCount: Math.max(0, wish.voteCount - 1) }
    : { ...wish, votedByMe: true, voteCount: wish.voteCount + 1 };
}

/** Can a creator claim it? Only open wishes nobody has claimed. */
export function canClaimWish(wish: Pick<WishView, 'status' | 'claimedBy'>): boolean {
  return wish.status === 'open' && !wish.claimedBy;
}
